import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { formatDate, formatMoney, parseDecimal } from "@bw/shared/utils";

import { StatusBadge } from "@/components/StatusBadge";

import type {
  PeriodReconciliationResponse,
  ReconciliationResponse,
} from "../api";

type ReconciliationRead = Readonly<{
  data: ReconciliationResponse | undefined;
  isError: boolean;
  isPending: boolean;
  refetch: () => unknown;
}>;

function Figure({ label, value }: { label: string; value: string }) {
  return (
    <Box component="div">
      <Typography component="dt" color="text.secondary" variant="caption">
        {label}
      </Typography>
      <Typography
        component="dd"
        variant="body2"
        sx={{ fontWeight: 600, m: 0, mt: 0.5 }}
      >
        {value}
      </Typography>
    </Box>
  );
}

function PeriodRow({
  period,
  envelopeCurrency,
}: {
  period: PeriodReconciliationResponse;
  envelopeCurrency: string | null;
}) {
  const currency = period.currency ?? envelopeCurrency ?? "USD";
  const expected = formatMoney(period.computed_net_owed, currency);
  const received = formatMoney(period.paid_out, currency);
  const difference = formatMoney(period.delta, currency);
  const isExact = parseDecimal(period.delta) === 0;
  const note = period.reconciliation_note?.trim();

  return (
    <Box component="li" sx={{ py: 2 }}>
      <Stack spacing={1.5}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          sx={{
            alignItems: { sm: "center" },
            justifyContent: "space-between",
          }}
        >
          <Typography color="text.secondary" variant="body2">
            {formatDate(period.period_start)} – {formatDate(period.period_end)}
          </Typography>
          <StatusBadge status={period.reconciliation_status} />
        </Stack>
        <Box
          component="dl"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(3, minmax(0, 1fr))",
            },
            gap: 2,
            m: 0,
          }}
        >
          {isExact ? (
            <Figure label="Expected and received" value={expected} />
          ) : (
            <>
              <Figure label="Expected" value={expected} />
              <Figure label="Received" value={received} />
            </>
          )}
          <Figure label="Difference" value={difference} />
        </Box>
        {note && (
          <Typography color="text.secondary" variant="caption">
            {note}
          </Typography>
        )}
      </Stack>
    </Box>
  );
}

export function OrgReconciliationSection({
  read,
}: {
  orgId: string;
  read: ReconciliationRead;
}) {
  const periods = read.data?.periods ?? [];
  const unmatched = read.data?.unmatched_payout_count ?? 0;
  const failed = read.data?.failed_payout_count ?? 0;

  return (
    <Paper
      component="section"
      aria-labelledby="reconciliation-heading"
      variant="outlined"
      sx={{ p: { xs: 2, md: 2.5 } }}
    >
      <Stack spacing={2}>
        <Typography
          id="reconciliation-heading"
          component="h2"
          variant="h6"
          sx={{ fontWeight: 700 }}
        >
          Reconciliation
        </Typography>

        {read.isPending ? (
          <Box
            role="status"
            aria-label="Loading reconciliation"
            aria-live="polite"
            sx={{ display: "flex", justifyContent: "center", py: 4 }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : read.isError ? (
          <Alert
            severity="error"
            role="alert"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => void read.refetch()}
              >
                Retry
              </Button>
            }
          >
            We couldn&apos;t load reconciliation. Try again.
          </Alert>
        ) : periods.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            No reconciliation data yet.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            <Typography color="text.secondary" variant="body2">
              Small gaps usually reflect Stripe processing fees deducted before
              payout. A large gap may indicate a payout issue worth reviewing.
            </Typography>

            {(unmatched > 0 || failed > 0) && (
              <Stack spacing={0.5}>
                {unmatched > 0 && (
                  <Typography color="text.secondary" variant="body2">
                    {unmatched} {unmatched === 1 ? "payout" : "payouts"} could
                    not be matched to a period.
                  </Typography>
                )}
                {failed > 0 && (
                  <Typography color="text.secondary" variant="body2">
                    {failed} failed {failed === 1 ? "payout" : "payouts"} in
                    this range.
                  </Typography>
                )}
              </Stack>
            )}

            <Stack
              component="ul"
              divider={<Divider flexItem />}
              sx={{ listStyle: "none", m: 0, p: 0 }}
            >
              {periods.map((period) => (
                <PeriodRow
                  key={`${period.period_start}-${period.period_end}-${period.currency}`}
                  period={period}
                  envelopeCurrency={read.data?.currency ?? null}
                />
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
